// src/lib/team/pto-list.ts
import { PTO_TAB_LABELS, type PtoTab } from "@/lib/enums";
import type { PtoStatus } from "@/lib/pto/approval-rules";
import { addDays, type IsoDate } from "@/lib/time/dates";

/** The My Team "PTO requests" panel: To approve | Upcoming | Past (DECISIONS 2026-10-15). */

/** One `ed-team-pto-requests` row. */
export interface PtoRequestView {
  id: string;
  personId: string;
  personName: string;
  typeEnum: string;
  typeName: string;
  startDate: IsoDate;
  endDate: IsoDate;
  hoursPerDay: number;
  description: string | null;
  status: PtoStatus;
  rejectionNote: string | null;
  /** The employee's reason when asking to cancel (status requestdelete). */
  deleteNote: string | null;
  approverName: string | null;
  days: number;
  needsHr: boolean;
  bookedDays: number;
  viewerIsHrApprover: boolean;
  changedOn: string | null;
}

/**
 * What an email's Approve / Reject link finds in the viewer's approvals: the row to decide, a
 * request already decided (or one this viewer cannot decide), or nothing under that id.
 */
export function emailDecision(
  rows: PtoRequestView[],
  id: string,
): { kind: "decide"; row: PtoRequestView } | { kind: "decided" } | { kind: "missing" } {
  const row = rows.find((r) => r.id.toLowerCase() === id.toLowerCase());
  if (!row) return { kind: "missing" };
  return canApprove(row) ? { kind: "decide", row } : { kind: "decided" };
}

/** Past shows decisions that ended in the last 180 days. @public */
export const PAST_DAYS = 180;

export function canApprove(r: PtoRequestView): boolean {
  return r.status === "pending" && (!r.needsHr || r.viewerIsHrApprover);
}

export function tabOf(r: PtoRequestView, today: IsoDate): PtoTab | null {
  if (r.status === "pending") return "pending";
  if (r.status === "indraft") return null;
  // A cancel ask waits on the approver (PTO plan decision 2).
  if (r.status === "requestdelete") return "pending";
  if (r.status === "rejected") return "past";
  return r.endDate >= today ? "upcoming" : "past";
}

export function requestsForTab(
  rows: PtoRequestView[],
  tab: PtoTab,
  today: IsoDate,
  showHolidays: boolean,
): PtoRequestView[] {
  const pastFrom = addDays(today, -PAST_DAYS);
  return (
    rows
      .filter((r) => tabOf(r, today) === tab)
      // Pending holidays always show: the decider must see everything awaiting a decision.
      .filter((r) => showHolidays || tab === "pending" || r.typeEnum !== "holiday")
      .filter((r) => tab !== "past" || r.endDate >= pastFrom)
      .sort(
        (a, b) =>
          (tab === "past"
            ? b.endDate.localeCompare(a.endDate)
            : a.startDate.localeCompare(b.startDate)) ||
          a.personName.localeCompare(b.personName) ||
          a.id.localeCompare(b.id),
      )
  );
}

/**
 * What waits on this viewer in To approve: requests they can approve plus cancel asks (any
 * approver may keep or remove one). HR-routed pending rows stay listed but are not counted.
 */
export function approvableCount(rows: PtoRequestView[]): number {
  return rows.filter((r) => canApprove(r) || isCancelAsk(r)).length;
}

/**
 * A tab's label. "To approve" carries `approvableCount`, the same number as the nav badge and
 * the banners (ed-pto-pending-count counts cancel asks too).
 */
export function ptoTabLabel(tab: PtoTab, rows: PtoRequestView[]): string {
  const n = tab === "pending" ? approvableCount(rows) : 0;
  return n > 0 ? `${PTO_TAB_LABELS[tab]} (${n})` : PTO_TAB_LABELS[tab];
}

export function defaultTab(rows: PtoRequestView[]): PtoTab {
  return rows.some((r) => r.status === "pending" || isCancelAsk(r)) ? "pending" : "upcoming";
}

const MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});
const label = (d: IsoDate) => MONTH_DAY.format(new Date(`${d}T00:00:00Z`));

/** "Nov 11", "Dec 28 – 31", "Nov 30 – Dec 1", "Dec 28, 2026 – Jan 2, 2027". */
export function formatDateRange(start: IsoDate, end: IsoDate): string {
  if (start === end) return label(start);
  if (start.slice(0, 4) !== end.slice(0, 4)) {
    return `${label(start)}, ${start.slice(0, 4)} – ${label(end)}, ${end.slice(0, 4)}`;
  }
  if (start.slice(0, 7) === end.slice(0, 7)) return `${label(start)} – ${Number(end.slice(8, 10))}`;
  return `${label(start)} – ${label(end)}`;
}

/** Approved, but booking did not finish (the Retry case). */
export function isUnbooked(r: PtoRequestView): boolean {
  return r.status === "approved" && r.bookedDays + 1e-9 < r.days;
}

/** Every row's Status chip; a pending request the viewer may decide reads "To approve". */
export function statusLabel(r: PtoRequestView): string {
  if (r.status === "approved") return "Approved";
  if (r.status === "rejected") return "Rejected";
  if (r.status === "requestdelete") return "Cancel asked";
  if (r.status === "pending" && !canApprove(r)) return "HR approves";
  return "To approve";
}

/** The employee asked to cancel approved time off; the approver keeps or removes it. */
export function isCancelAsk(r: PtoRequestView): boolean {
  return r.status === "requestdelete";
}

/** The approver or HR may remove approved time off that is not over yet, or a cancel ask (D2, D3). */
export function canRemove(r: PtoRequestView, today: IsoDate): boolean {
  if (r.status === "requestdelete") return true;
  return r.status === "approved" && r.endDate >= today;
}
